<template>
  <div
    class="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-y-2 tw-px-4 tw-py-3 tw-bg-white"
    :class="compact ? 'tw-gap-x-3' : 'tw-gap-x-6'"
  >
    <!-- Rows per page selector -->
    <div class="tw-flex tw-items-center tw-gap-2">
      <span v-if="!compact" class="tw-text-sm tw-font-medium tw-text-gray-700 tw-text-nowrap">
        {{ t('common.pagination.rows_per_page') }}
      </span>
      <Select
        :model-value="table.getState().pagination.pageSize.toString()"
        @update:model-value="value => table.setPageSize(Number(value))"
      >
        <SelectTrigger class="tw-w-20">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem v-for="size in pageSizes" :key="size" :value="size.toString()">
              {{ size }}
            </SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>

    <!-- Pagination info and controls -->
    <div class="tw-flex tw-items-center tw-gap-4">
      <!-- Page info -->
      <div v-if="!compact" class="tw-flex tw-items-center tw-gap-2">
        <span class="tw-text-xs tw-text-gray-700 tw-whitespace-nowrap">
          {{
            t('common.pagination.page_info', {
              currentPage: currentPage,
              totalPages: totalPages,
              totalItems: totalItems,
            })
          }}
        </span>
      </div>

      <!-- Navigation controls using shadcn-vue pagination -->
      <Pagination
        :total="totalItems"
        :items-per-page="table.getState().pagination.pageSize"
        :default-page="currentPage"
        :page="currentPage"
        @update:page="handlePageChange"
        class="tw-w-auto"
      >
        <PaginationContent v-slot="{ items }" class="tw-gap-1">
          <!-- First page button -->
          <PaginationItem v-if="!compact" :value="1" @click="table.setPageIndex(0)">
            <button
              :disabled="!table.getCanPreviousPage()"
              class="tw-inline-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-md tw-border tw-border-gray-300 tw-bg-white tw-text-sm tw-font-medium tw-text-gray-500 tw-transition-colors hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="16"
                color="currentColor"
                fill="none"
              >
                <path
                  d="M11.5 18C11.5 18 5.50001 13.5811 5.5 12C5.49999 10.4188 11.5 6 11.5 6"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                ></path>
                <path
                  d="M18.5 18C18.5 18 12.5 13.5811 12.5 12C12.5 10.4188 18.5 6 18.5 6"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                ></path>
              </svg>
            </button>
          </PaginationItem>

          <!-- Previous page -->
          <PaginationPrevious :disabled="!table.getCanPreviousPage()">
            <template #default>
              <ChevronLeft v-if="compact" class="tw-h-4 tw-w-4" />
              <template v-else>{{ t('common.forms.previous') }}</template>
            </template>
          </PaginationPrevious>

          <!-- Page numbers (full mode) -->
          <template v-if="!compact">
            <template v-for="(item, index) in items" :key="index">
              <PaginationItem
                v-if="item.type === 'page'"
                :value="item.value"
                :is-active="item.value === currentPage"
                :class="
                  item.value === currentPage
                    ? '!tw-bg-secondary-600 !tw-text-white !tw-border !tw-border-secondary-600'
                    : '!tw-bg-white !tw-text-gray-700 !tw-border !tw-border-gray-300 hover:tw-bg-gray-50'
                "
              >
                {{ item.value }}
              </PaginationItem>
              <PaginationEllipsis v-else-if="item.type === 'ellipsis'" :index="index" />
            </template>
          </template>

          <!-- Compact current-page indicator -->
          <span
            v-else
            class="tw-whitespace-nowrap tw-px-1.5 tw-text-sm tw-font-semibold tw-tabular-nums tw-text-secondary-700 dark:tw-text-neutral-200"
          >
            {{ currentPage }} / {{ totalPages }}
          </span>

          <!-- Next page -->
          <PaginationNext :disabled="!table.getCanNextPage()">
            <template #default>
              <ChevronRight v-if="compact" class="tw-h-4 tw-w-4" />
              <template v-else>{{ t('common.forms.next') }}</template>
            </template>
          </PaginationNext>

          <!-- Last page button -->
          <PaginationItem
            v-if="!compact"
            :value="totalPages"
            @click="table.setPageIndex(table.getPageCount() - 1)"
          >
            <button
              :disabled="!table.getCanNextPage()"
              class="tw-inline-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-md tw-border tw-border-gray-300 tw-bg-white tw-text-sm tw-font-medium tw-text-gray-500 tw-transition-colors hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="16"
                color="currentColor"
                fill="none"
              >
                <path
                  d="M12.5 18C12.5 18 18.5 13.5811 18.5 12C18.5 10.4188 12.5 6 12.5 6"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                ></path>
                <path
                  d="M5.50005 18C5.50005 18 11.5 13.5811 11.5 12C11.5 10.4188 5.5 6 5.5 6"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                ></path>
              </svg>
            </button>
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Table } from '@tanstack/vue-table';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ChevronLeft, ChevronRight } from 'lucide-vue-next';

const { t } = useI18n();

const props = defineProps<{
  table: Table<any>;
  pageSizes?: number[];
  /** Compact mode: hide numbered pages + first/last, show only prev / "X of Y" / next on one line. */
  compact?: boolean;
}>();

const pageSizes = computed(() => props.pageSizes || [10, 25, 50, 100]);

const currentPage = computed(() => props.table.getState().pagination.pageIndex + 1);
const totalPages = computed(() => props.table.getPageCount());
const totalItems = computed(() => props.table.getRowCount());

const handlePageChange = (page: number) => {
  props.table.setPageIndex(page - 1);
};
</script>
